import { medianInt, percentileInt } from "../analytics/perf-monitor.js";

export const SKY_AUTO_PROBE_ROLLING_MS = 1000;
export const SKY_AUTO_PROBE_WARMUP_MS = 1000;
export const SKY_AUTO_PROBE_P95_BUDGET_MULT = 1.25;

/**
 * Samples presentation FPS (first-Fall countdown probe, or cycle upgrade sampler)
 * and decides whether dynamic sky motion is affordable.
 *
 * @param {{
 *   fpsThreshold?: number,
 *   displayHz?: number | null,
 *   warmupMs?: number,
 *   frameMsP95Max?: number | null,
 *   p95BudgetMult?: number,
 *   droppedFrameRatioMax?: number,
 * }} [options]
 */
export function createSkyAutoProbe(options) {
  options = options || {};
  var configuredThreshold = options.fpsThreshold != null ? Number(options.fpsThreshold) : 60;
  if (!isFinite(configuredThreshold) || configuredThreshold <= 0) configuredThreshold = 60;

  var displayHz = options.displayHz != null ? Number(options.displayHz) : null;
  if (displayHz != null && (!isFinite(displayHz) || displayHz <= 0)) displayHz = null;

  var effectiveThreshold = configuredThreshold;
  if (displayHz != null) {
    effectiveThreshold = Math.min(displayHz, configuredThreshold);
  }

  var warmupMs = options.warmupMs != null ? Number(options.warmupMs) : SKY_AUTO_PROBE_WARMUP_MS;
  if (!isFinite(warmupMs) || warmupMs < 0) warmupMs = SKY_AUTO_PROBE_WARMUP_MS;

  var frameMsP95Max = options.frameMsP95Max != null ? Number(options.frameMsP95Max) : null;
  var p95BudgetMult = options.p95BudgetMult != null
    ? Number(options.p95BudgetMult)
    : SKY_AUTO_PROBE_P95_BUDGET_MULT;
  if (!isFinite(p95BudgetMult) || p95BudgetMult <= 0) p95BudgetMult = SKY_AUTO_PROBE_P95_BUDGET_MULT;
  var droppedFrameRatioMax = options.droppedFrameRatioMax != null
    ? Number(options.droppedFrameRatioMax)
    : 0.15;
  if (!isFinite(droppedFrameRatioMax) || droppedFrameRatioMax < 0) droppedFrameRatioMax = 0.15;

  var frameBudgetMs = 1000 / effectiveThreshold;
  var resolvedP95Max = frameMsP95Max != null && isFinite(frameMsP95Max) && frameMsP95Max > 0
    ? frameMsP95Max
    : Math.ceil(frameBudgetMs * p95BudgetMult);

  var startedAt = null;
  var rollSince = null;
  var rollFrames = 0;
  /** @type {number[]} */
  var fpsSamples = [];
  /** @type {number[]} */
  var frameMsSamples = [];
  var droppedFrames = 0;
  var totalFrames = 0;

  function resetRolling(now) {
    rollSince = now;
    rollFrames = 0;
  }

  function recomputeBudget() {
    effectiveThreshold = displayHz != null
      ? Math.min(displayHz, configuredThreshold)
      : configuredThreshold;
    frameBudgetMs = 1000 / effectiveThreshold;
    if (frameMsP95Max == null || !isFinite(frameMsP95Max) || frameMsP95Max <= 0) {
      resolvedP95Max = Math.ceil(frameBudgetMs * p95BudgetMult);
    }
  }

  return {
    /**
     * @param {number} now
     * @param {number} [frameMs]
     */
    tick: function (now, frameMs) {
      if (!isFinite(now)) return;
      if (startedAt == null) startedAt = now;
      if (rollSince == null) resetRolling(now);

      var pastWarmup = now - startedAt >= warmupMs;
      if (frameMs != null && isFinite(frameMs)) {
        var delta = Math.max(0, Math.min(100, frameMs));
        if (pastWarmup) {
          frameMsSamples.push(delta);
          totalFrames += 1;
          if (delta > frameBudgetMs) droppedFrames += 1;
        }
      }

      rollFrames += 1;
      var elapsed = now - rollSince;
      if (elapsed < 16) return;
      if (elapsed >= SKY_AUTO_PROBE_ROLLING_MS) {
        if (pastWarmup) {
          fpsSamples.push(Math.round((rollFrames / elapsed) * 1000));
        }
        resetRolling(now);
      }
    },

    /**
     * @returns {{
     *   passed: boolean,
     *   medianFps: number,
     *   fpsThreshold: number,
     *   frameMsP95: number,
     *   frameMsP95Max: number,
     *   droppedFrameRatio: number,
     *   droppedFrameRatioMax: number,
     *   displayHz: number | null,
     *   sampleCount: number,
     * }}
     */
    finish: function () {
      var medianFps = fpsSamples.length > 0 ? medianInt(fpsSamples) : 60;
      var frameMsP95 = frameMsSamples.length > 0
        ? percentileInt(frameMsSamples, 95)
        : 0;
      var droppedRatio = totalFrames > 0 ? droppedFrames / totalFrames : 0;
      var sampleCount = frameMsSamples.length;

      var passed = sampleCount > 0
        && frameMsP95 <= resolvedP95Max
        && droppedRatio <= droppedFrameRatioMax;

      if (sampleCount === 0 && fpsSamples.length > 0) {
        passed = medianFps >= effectiveThreshold;
      }

      return {
        passed: passed,
        medianFps: medianFps,
        fpsThreshold: effectiveThreshold,
        frameMsP95: frameMsP95,
        frameMsP95Max: resolvedP95Max,
        droppedFrameRatio: droppedRatio,
        droppedFrameRatioMax: droppedFrameRatioMax,
        displayHz: displayHz,
        sampleCount: sampleCount,
      };
    },

    /** @param {number} hz */
    setDisplayHz: function (hz) {
      if (hz == null || !isFinite(hz) || hz <= 0) return;
      displayHz = hz;
      recomputeBudget();
    },
  };
}